import axios from 'axios';
import { 
  NameNumerologyOrder, 
  LoveCompatibilityOrder, 
  PaymentStatus, 
  FilterParams
} from '../types';
import config from '../config/environment';

const API_BASE_URL = config.API_BASE_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Payment Status
export const getPaymentStatus = async (orderId: string): Promise<PaymentStatus> => {
  const response = await api.get(`/api/payment/status/${orderId}`);
  return response.data;
};

// Delete User Detail
export const deleteUserDetail = async (orderId: string): Promise<void> => {
  await api.delete(`/api/user-details/${orderId}`);
};

// Update Name Numerology Order
export const updateNameNumerologyOrder = async (orderId: string, data: Partial<NameNumerologyOrder>): Promise<void> => {
  await api.put(`/api/user-details/name-numerology/${orderId}`, data);
};

// Update Love Compatibility Order
export const updateLoveCompatibilityOrder = async (orderId: string, data: Partial<LoveCompatibilityOrder>): Promise<void> => {
  await api.put(`/api/user-details/compatibility/${orderId}`, data);
};

// Love Compatibility Orders with pagination
export const getCompatibilityOrders = async (filters?: FilterParams & { page?: number; limit?: number }): Promise<{ data: LoveCompatibilityOrder[], pagination: any }> => {
  try {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '' && key !== 'page') {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get(`/api/user-details/compatibility?${params.toString()}`);
    console.log('Compatibility API Response:', response.data);
    
    // Return the response with pagination data
    return response.data;
  } catch (error) {
    console.error('Error fetching compatibility orders:', error);
    throw error;
  }
};

export const getCompatibilityOrderById = async (orderId: string): Promise<LoveCompatibilityOrder> => {
  const response = await api.get(`/api/user-details/compatibility/${orderId}`);
  return response.data;
};

export const getCompatibilityOrdersByDate = async (
  date: string, 
  filters?: Omit<FilterParams, 'limit' | 'offset'>
): Promise<LoveCompatibilityOrder[]> => {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });
  }
  
  const response = await api.get(`/api/user-details/compatibility/date/${date}?${params.toString()}`);
  return response.data;
};

// Enhanced search for compatibility orders
export const searchCompatibilityOrders = async (
  searchTerm: string,
  filters?: FilterParams & { page?: number; limit?: number }
): Promise<{ data: LoveCompatibilityOrder[], pagination: any }> => {
  try {
    const params = new URLSearchParams();
    params.append('search_term', searchTerm);
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '' && key !== 'page' && key !== 'search_term') {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get(`/api/user-details/compatibility/search?${params.toString()}`);
    console.log('Compatibility Search API Response:', response.data);
    
    return response.data;
  } catch (error) {
    console.error('Error searching compatibility orders:', error);
    throw error;
  }
};

// Enhanced search for name numerology orders
export const searchNameNumerologyOrders = async (
  searchTerm: string,
  filters?: FilterParams & { page?: number; limit?: number }
): Promise<{ data: NameNumerologyOrder[], pagination: any }> => {
  try {
    const params = new URLSearchParams();
    params.append('search_term', searchTerm);
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '' && key !== 'page' && key !== 'search_term') {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get(`/api/user-details/name-numerology/search?${params.toString()}`);
    console.log('Numerology Search API Response:', response.data);
    
    return response.data;
  } catch (error) {
    console.error('Error searching name numerology orders:', error);
    throw error;
  }
};

// Name Numerology Orders with pagination
export const getNameNumerologyOrders = async (filters?: FilterParams & { page?: number; limit?: number }): Promise<{ data: NameNumerologyOrder[], pagination: any }> => {
  try {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '' && key !== 'page') {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get(`/api/user-details/name-numerology?${params.toString()}`);
    console.log('Numerology API Response:', response.data);
    
    // Return the response with pagination data
    return response.data;
  } catch (error) {
    console.error('Error fetching numerology orders:', error);
    throw error;
  }
};

export const getNameNumerologyOrderById = async (orderId: string): Promise<NameNumerologyOrder> => {
  const response = await api.get(`/api/user-details/name-numerology/${orderId}`);
  return response.data;
};

export const getNameNumerologyOrdersByDate = async (
  date: string, 
  filters?: Omit<FilterParams, 'limit' | 'offset'>
): Promise<NameNumerologyOrder[]> => {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });
  }
  
  const response = await api.get(`/api/user-details/name-numerology/date/${date}?${params.toString()}`);
  return response.data;
};

export const validateOrders = async (startDate: string, endDate: string): Promise<any> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/payment/validateOrders/${startDate}/${endDate}`);
    return response.data;
  } catch (error) {
    console.error('Error validating orders:', error);
    throw error;
  }
};



export default api;
